import { useCallback, useMemo } from 'react';
import _get from 'lodash/get';
import {
  wrapModule,
  getModuleEnvironment,
} from '@grabjs/mobile-kit-bridge-sdk';

const baseResponse = {
  result: null,
  isSuccess: false,
  isError: false,
  isMethodNotExistError: false,
};

const generateSuccessResponse = result => ({
  ...baseResponse,
  isSuccess: true,
  result,
});

export const useNativeModule = JSInterfaceName => {
  const wrappedModuleName = `Wrapped${JSInterfaceName}`;

  if (!window[wrappedModuleName]) wrapModule(window, JSInterfaceName);

  const invoke = useCallback(
    async (name, params) => {
      try {
        // NOTE:: The promise might never resolve here if native didn't invoke the callback
        const response = await window[wrappedModuleName].invoke(name, params);

        if (response.status_code > 299) throw new Error('Response not ok');

        return generateSuccessResponse(response.result);
      } catch (error) {
        if (error && /Unexpected method/.test(error.message || ''))
          return {
            ...baseResponse,
            isError: true,
            isMethodNotExistError: true,
          };

        return { ...baseResponse, isError: true };
      }
    },
    [wrappedModuleName],
  );

  const inApp = useMemo(
    () => getModuleEnvironment(window, JSInterfaceName) !== undefined,
    [JSInterfaceName],
  );

  return { invoke, inApp };
};

export const useAuthModule = () => {
  const { invoke, inApp } = useNativeModule('AuthModule');

  const getAccessToken = useCallback(
    async () => {
      const response = await invoke('getAccessToken', {});
      return response;
    },
    [invoke],
  );

  return { getAccessToken, inApp };
};

export const useRTCModule = () => {
  const { invoke, inApp } = useNativeModule('RTCModule');

  const callDriver = useCallback(
    async params => {
      const response = await invoke('call', {
        bookingCode: _get(params, 'bookingCode', ''),
        phoneNumber: _get(params, 'phoneNumber', ''),
        remoteUserId: _get(params, 'driverVoipId', ''),
        callerName: _get(params, 'callerName', ''),
        calleeName: _get(params, 'calleeName', ''),
        voipProvider: _get(params, 'voipProvider', ''),
      });
      return response;
    },
    [invoke],
  );

  const openChatScreen = useCallback(
    async params => {
      const response = await invoke('openChatScreen', {
        bookingCode: _get(params, 'bookingCode', ''),
        chatId: _get(params, 'chatRoomId', ''),
        driverId: _get(params, 'driverId', ''),
        driverName: _get(params, 'driverName', ''),
        driverImageUrl: _get(params, 'driverProfileSrc', ''),
        phoneNumber: _get(params, 'phoneNumber', ''),
        driverPhoneNumberForCall: _get(params, 'phoneNumber', ''),
        driverPhoneNumberForSMS: _get(params, 'phoneNumber', ''),
        vehiclePlateNumber: _get(params, 'plateNumber', ''),
        vehicleModel: _get(params, 'model', ''),
        caseNumber: _get(params, 'caseNumber', ''),
        rating: _get(params, 'rating', ''),
        calleeVoipID: _get(params, 'calleeVoipId', ''),
      });
      return response;
    },
    [invoke],
  );

  return { callDriver, openChatScreen, inApp };
};

export const usePaymentCheckoutModule = () => {
  const { invoke, inApp } = useNativeModule('PaymentCheckoutModule');

  const proceedToPay = useCallback(
    params =>
      invoke('proceedToPay', {
        orderID: _get(params, 'orderId', ''),
      }),
    [invoke],
  );

  return { proceedToPay, inApp };
};

export const useExperimentModule = () => {
  const { invoke } = useNativeModule('ExperimentModule');

  const getBoolValue = useCallback(
    params =>
      invoke('getBoolValue', {
        name: _get(params, 'name', ''),
        defaultValue: false,
      }),
    [invoke],
  );

  return { getBoolValue };
};

export default {};
